import { toTask } from '@execonline-inc/maybe-adapter';
import { nothing } from 'maybeasy';
import { Task } from 'taskarian';
import { v4 as uuid } from 'uuid';
import { callApi } from '../../../../../Appy';
import { currentUserStore } from '../../../../../CurrentUserStore';
import { findLinkByT } from '../../../../../Links';
import { Link } from '../../../../../Resource/Types';
import { learningCollectionEncoder } from '../../../LearningCollection/CurrentCollectionStep/BuilderV2/Encoders';
import {
  LearningCollectionResourceWithScheduledPrograms,
  ProductCollectionResource,
} from '../../../LearningCollection/CurrentCollectionStep/BuilderV2/Types';
import { cloneTemplateResourceDecoder, clonedCollectionResourceDecoder } from './Decoders';
import CloneStore from './Store';

// Custom sections are sent as 'blank' with no id: the create endpoint's
// by-id lookup (LearningDesignPolicy::Scope) only resolves designs exposed
// through organizations_products, which org-built custom sections are not,
// so handing them back by id 404s. The 'blank' path rebuilds the section
// from the name/description/display/experiences we already carry. Curated
// and browse-all sections are rebuilt/regenerated by the backend from
// their state as-is.
const asCloneableSection = (
  productCollection: ProductCollectionResource,
): ProductCollectionResource =>
  productCollection.payload.learning_design_state === 'custom'
    ? {
        ...productCollection,
        payload: {
          ...productCollection.payload,
          id: nothing(),
          learning_design_state: 'blank',
        },
      }
    : productCollection;

const asCloneOf = (
  resource: LearningCollectionResourceWithScheduledPrograms,
): LearningCollectionResourceWithScheduledPrograms => ({
  payload: {
    ...resource.payload,
    id: nothing(),
    name: `Copy of ${resource.payload.name}`,
    published_at: nothing(),
    // Mint a fresh draft-linkage key instead of copying the source's (the
    // create endpoint persists this verbatim and does not backfill blanks).
    client_identifier: uuid(),
    product_collections: resource.payload.product_collections.map(asCloneableSection),
  },
  links: resource.links,
});

// Overrides on top of the shared encoder: browse_all is echoed because the
// create endpoint falls back to the column default (true) when it is omitted;
// source_identifier is stamped 'clone' (a distinct collection source the
// backend treats as license-optional like builder-v2); and
// cloned_from_use_case_id records provenance back to the source collection.
const clonePayload = (
  resource: LearningCollectionResourceWithScheduledPrograms,
  sourceId: number,
) => {
  const encoded = learningCollectionEncoder(asCloneOf(resource), { kind: 'none' });
  return {
    learning_collection: {
      ...encoded.learning_collection,
      browse_all: resource.payload.browse_all,
      source_identifier: 'clone' as const,
      cloned_from_use_case_id: sourceId,
    },
  };
};

// DVLP-7981: the create endpoint keeps only the courses the organization can
// still offer, so a source carrying retired or unlicensed courses comes back
// smaller. That earns the same "review this collection's courses" notice the
// clone already shows when courses could not be copied.
const droppedCourses = (
  source: LearningCollectionResourceWithScheduledPrograms,
  keptExperienceIds: number[],
): boolean => {
  const kept = new Set(keptExperienceIds);
  return source.payload.product_collections
    .flatMap((pc) => pc.payload.experiences)
    .some((id) => !kept.has(id));
};

export const cloneLearningCollection = (
  id: number,
  collectionName: string,
  store: CloneStore,
): void => {
  store.cloning(collectionName);

  Task.succeed<{}, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkByT({ rel: 'edit-learning-collection-template-v2', method: 'get' }))
    .map((link) => ({ ...link, href: link.href.replace('{learning_collection_id}', `${id}`) }))
    .andThen(callApi(cloneTemplateResourceDecoder, {}))
    .andThen((resource) =>
      Task.succeed<{}, ReadonlyArray<Link>>(currentUserStore.links)
        .andThen(findLinkByT({ rel: 'create-learning-collection-template-v2', method: 'post' }))
        .andThen(callApi(clonedCollectionResourceDecoder, clonePayload(resource, id)))
        .andThen(({ payload }) =>
          toTask({ kind: 'missing-id-error' }, payload.id).map((newCollectionId) => ({
            newCollectionId,
            coursesNeedReview:
              resource.payload.has_directly_attached_experiences ||
              droppedCourses(resource, payload.experienceIds),
          })),
        ),
    )
    .fork(
      () => store.failed(),
      ({ newCollectionId, coursesNeedReview }) => store.cloned(newCollectionId, coursesNeedReview),
    );
};
